import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, CheckCircle2, XCircle, Clock, DollarSign, Phone, Mail, MapPin, Edit2, Save, X as XIcon, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Use environment variable with fallback to current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CompaniesManagement() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [activeFilter, setActiveFilter] = useState("all"); // "all", "active", "inactive"
  const [approvalFilter, setApprovalFilter] = useState("all"); // "all", "pending", "approved", "rejected"
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordCompany, setResetPasswordCompany] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetMethod, setResetMethod] = useState("email"); // "email" or "manual"

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompanies(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to load companies");
      setLoading(false);
    }
  };

  const handleApproval = async (companyId, status) => {
    try {
      await axios.put(
        `${API}/companies/${companyId}/approve`,
        { approval_status: status },
        { withCredentials: true }
      );
      
      toast.success(`Company ${status === "approved" ? "approved" : "rejected"}`);
      fetchCompanies();
      setSelectedCompany(null);
    } catch (error) {
      console.error("Error updating company:", error);
      toast.error("Failed to update company");
    }
  };

  const handlePaymentUpdate = async (companyId, status) => {
    try {
      await axios.put(
        `${API}/companies/${companyId}/payment?payment_status=${status}`,
        {},
        { withCredentials: true }
      );
      
      toast.success("Payment status updated");
      fetchCompanies();
      setSelectedCompany(null);
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error("Failed to update payment");
    }
  };

  const handleToggleActive = async (companyId, isActive) => {
    try {
      await axios.put(
        `${API}/companies/${companyId}/toggle-active?is_active=${isActive}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(isActive ? "Company activated" : "Company deactivated");
      fetchCompanies();
    } catch (error) {
      console.error("Error toggling active status:", error);
      toast.error("Failed to update company status");
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    console.log("[Delete Company] Starting deletion for:", companyId, companyName);
    
    if (!window.confirm(`Are you sure you want to permanently delete "${companyName}"? This action cannot be undone and will remove all associated data.`)) {
      console.log("[Delete Company] User cancelled deletion");
      return;
    }

    console.log("[Delete Company] User confirmed, sending DELETE request");

    try {
      const response = await axios.delete(`${API}/companies/${companyId}`, { withCredentials: true });
      console.log("[Delete Company] DELETE response:", response.data);
      toast.success(`Company "${companyName}" deleted successfully`);
      setDialogOpen(false);
      setSelectedCompany(null);
      await fetchCompanies();
      console.log("[Delete Company] Completed successfully");
    } catch (error) {
      console.error("[Delete Company] Error:", error);
      console.error("[Delete Company] Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to delete company");
    }
  };


  const handleSendResetEmail = async () => {
    try {
      await axios.post(
        `${API}/admin/companies/${resetPasswordCompany.id}/send-reset-email`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Password reset email sent to ${resetPasswordCompany.email}`);
      setResetPasswordDialogOpen(false);
      setResetPasswordCompany(null);
      setResetMethod("email");
    } catch (error) {
      console.error("Error sending reset email:", error);
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await axios.post(
        `${API}/admin/companies/${resetPasswordCompany.id}/reset-password`,
        { new_password: newPassword },
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      toast.success(`Password reset successfully for ${resetPasswordCompany.company_name}`);
      setResetPasswordDialogOpen(false);
      setResetPasswordCompany(null);
      setNewPassword("");
      setResetMethod("email");
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };



  const startEdit = (company) => {
    setEditForm({
      company_name: company.company_name || '',
      contact_person: company.contact_person || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      website: company.website || '',
      description: company.description || '',
      cities: company.cities?.join(', ') || '',
      postal_code_prefixes: company.postal_code_prefixes?.join(', ') || '',
      management_types: company.management_types?.join(', ') || '',
      service_radius_km: company.service_radius_km || 50,
      is_active: company.is_active,
      approval_status: company.approval_status,
      payment_status: company.payment_status
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const saveEdit = async (companyId) => {
    try {
      const params = new URLSearchParams();
      Object.keys(editForm).forEach(key => {
        if (editForm[key] !== undefined && editForm[key] !== null) {
          params.append(key, editForm[key]);
        }
      });

      await axios.put(
        `${API}/admin/companies/${companyId}?${params.toString()}`,
        {},
        { withCredentials: true }
      );
      
      toast.success("Company updated successfully");
      setIsEditing(false);
      setEditForm({});
      fetchCompanies();
      setSelectedCompany(null);
    } catch (error) {
      console.error("Error updating company:", error);
      toast.error("Failed to update company");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
      approved: "bg-green-100 text-green-700 border-green-200",
      rejected: "bg-blue-100 text-blue-700 border-blue-200",
      active: "bg-green-100 text-green-700 border-green-200",
      paid: "bg-blue-100 text-blue-700 border-blue-200",
      suspended: "bg-gray-100 text-gray-700 border-gray-200"
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  const activeCompanies = companies.filter(c => c.is_active === true);
  const inactiveCompanies = companies.filter(c => c.is_active === false);
  const approvedCompanies = companies.filter(c => c.approval_status === "approved");
  const pendingCompanies = companies.filter(c => c.approval_status === "pending");
  const rejectedCompanies = companies.filter(c => c.approval_status === "rejected");

  // Filter companies based on active and approval filters
  const filteredCompanies = companies.filter(c => {
    // Active filter
    if (activeFilter === "active" && c.is_active !== true) return false;
    if (activeFilter === "inactive" && c.is_active !== false) return false;
    
    // Approval filter
    if (approvalFilter === "pending" && c.approval_status !== "pending") return false;
    if (approvalFilter === "approved" && c.approval_status !== "approved") return false;
    if (approvalFilter === "rejected" && c.approval_status !== "rejected") return false;
    
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Stats with Filter Buttons */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card 
          className={`cursor-pointer transition-all ${activeFilter === "all" && approvalFilter === "all" ? "bg-blue-50 border-blue-300 ring-2 ring-blue-200" : "bg-white border-slate-200 hover:shadow-md"}`}
          onClick={() => {
            setActiveFilter("all");
            setApprovalFilter("all");
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">All Companies</p>
                <p className="text-3xl font-bold text-slate-900">{companies.length}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${activeFilter === "active" && approvalFilter === "all" ? "bg-green-50 border-green-300 ring-2 ring-green-200" : "bg-white border-slate-200 hover:shadow-md"}`}
          onClick={() => {
            setActiveFilter("active");
            setApprovalFilter("all");
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">🟢 Active</p>
                <p className="text-3xl font-bold text-green-700">{activeCompanies.length}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${activeFilter === "inactive" && approvalFilter === "all" ? "bg-gray-50 border-gray-300 ring-2 ring-gray-200" : "bg-white border-slate-200 hover:shadow-md"}`}
          onClick={() => {
            setActiveFilter("inactive");
            setApprovalFilter("all");
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">⚫ Inactive</p>
                <p className="text-3xl font-bold text-gray-700">{inactiveCompanies.length}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${approvalFilter === "pending" && activeFilter === "all" ? "bg-yellow-50 border-yellow-300 ring-2 ring-yellow-200" : "bg-white border-slate-200 hover:shadow-md"}`}
          onClick={() => {
            setApprovalFilter("pending");
            setActiveFilter("all");
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">⏳ Pending Approval</p>
                <p className="text-3xl font-bold text-yellow-700">{pendingCompanies.length}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals */}
      {pendingCompanies.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Companies Pending Approval ({pendingCompanies.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingCompanies.map((company) => (
                <Dialog key={company.id} onOpenChange={(open) => open && setSelectedCompany(company)}>
                  <DialogTrigger asChild>
                    <div
                      className="bg-white p-4 rounded-lg border border-amber-200 hover:shadow-md transition-all cursor-pointer"
                      data-testid={`pending-company-${company.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">{company.company_name}</h4>
                          <p className="text-sm text-slate-600">{company.contact_person} • {company.email}</p>
                        </div>
                        <Button size="sm" variant="outline">
                          Review
                        </Button>
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Review Company Application</DialogTitle>
                    </DialogHeader>
                    {selectedCompany && selectedCompany.id === company.id && (
                      <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-slate-500">Company Name</p>
                            <p className="font-medium text-slate-900">{selectedCompany.company_name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Contact Person</p>
                            <p className="font-medium text-slate-900">{selectedCompany.contact_person}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Email</p>
                            <p className="font-medium text-slate-900">{selectedCompany.email}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Phone</p>
                            <p className="font-medium text-slate-900">{selectedCompany.phone}</p>
                          </div>
                          <div className="md:col-span-2">
                            <p className="text-sm text-slate-500">Address</p>
                            <p className="font-medium text-slate-900">{selectedCompany.address}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Cities</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {selectedCompany.cities.map((city, i) => (
                                <Badge key={i} variant="outline" className="bg-blue-50">
                                  {city}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Postal Codes</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {selectedCompany.postal_code_prefixes.map((pc, i) => (
                                <Badge key={i} variant="outline" className="bg-purple-50">
                                  {pc}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          {selectedCompany.description && (
                            <div className="md:col-span-2">
                              <p className="text-sm text-slate-500">Description</p>
                              <p className="text-slate-900">{selectedCompany.description}</p>
                            </div>
                          )}
                          {selectedCompany.website && (
                            <div className="md:col-span-2">
                              <p className="text-sm text-slate-500">Website</p>
                              <a
                                href={selectedCompany.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:underline"
                              >
                                {selectedCompany.website}
                              </a>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-3 pt-4 border-t">
                          <Button
                            onClick={() => handleApproval(selectedCompany.id, "approved")}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            data-testid="approve-button"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleApproval(selectedCompany.id, "rejected")}
                            variant="outline"
                            className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid="reject-button"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtered Companies */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {activeFilter === "all" && "All Companies"}
              {activeFilter === "active" && "🟢 Active Companies"}
              {activeFilter === "inactive" && "⚫ Inactive Companies"}
              <span className="text-slate-500 font-normal ml-2">({filteredCompanies.length})</span>
            </CardTitle>
            {activeFilter !== "all" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveFilter("all")}
              >
                Show All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredCompanies.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>
                  {activeFilter === "active" && "No active companies"}
                  {activeFilter === "inactive" && "No inactive companies"}
                  {activeFilter === "all" && "No companies registered yet"}
                </p>
              </div>
            ) : (
              filteredCompanies.map((company) => (
                <Dialog 
                  key={company.id} 
                  open={dialogOpen && selectedCompany?.id === company.id}
                  onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (open) {
                      setSelectedCompany(company);
                    } else {
                      setSelectedCompany(null);
                      setIsEditing(false);
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <div
                      className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                      data-testid={`company-${company.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-slate-900">{company.company_name}</h4>
                            <Badge className={getStatusColor(company.approval_status)} variant="outline">
                              {company.approval_status}
                            </Badge>
                            <Badge className={getStatusColor(company.payment_status)} variant="outline">
                              {company.payment_status}
                            </Badge>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleActive(company.id, !company.is_active);
                              }}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                company.is_active 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              title={company.is_active ? 'Click to deactivate' : 'Click to activate'}
                            >
                              {company.is_active ? '🟢 Active' : '⚫ Inactive'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCompany(company.id, company.company_name);
                              }}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors border border-blue-200"
                              title="Delete company"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              {company.email}
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4" />
                              {company.phone}
                            </div>
                            <div className="flex items-center gap-2 md:col-span-2">
                              <MapPin className="h-4 w-4" />
                              {company.address}
                            </div>
                            {company.created_at && (
                              <div className="flex items-center gap-2 text-xs text-slate-500 md:col-span-2">
                                <Calendar className="h-3 w-3" />
                                <span>Joined: {new Date(company.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <div className="flex items-center justify-between">
                        <DialogTitle>Company Details</DialogTitle>
                        {!isEditing ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEdit(selectedCompany)}
                            className="flex items-center gap-2"
                          >
                            <Edit2 className="h-4 w-4" />
                            Edit
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveEdit(selectedCompany.id)}
                              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                            >
                              <Save className="h-4 w-4" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEdit}
                              className="flex items-center gap-2"
                            >
                              <XIcon className="h-4 w-4" />
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    </DialogHeader>
                    {selectedCompany && selectedCompany.id === company.id && (
                      <div className="space-y-6">
                        {!isEditing && (
                          <div className="flex items-center gap-3">
                            <Badge className={getStatusColor(selectedCompany.approval_status)} variant="outline">
                              {selectedCompany.approval_status}
                            </Badge>
                            <Badge className={getStatusColor(selectedCompany.payment_status)} variant="outline">
                              {selectedCompany.payment_status}
                            </Badge>
                          </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Company Name */}
                          <div>
                            <Label className="text-sm text-slate-500">Company Name</Label>
                            {isEditing ? (
                              <Input
                                value={editForm.company_name || ''}
                                onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                                className="mt-1"
                              />
                            ) : (
                              <p className="font-medium text-slate-900 mt-1">{selectedCompany.company_name}</p>
                            )}
                          </div>
                          
                          {/* Contact Person */}
                          <div>
                            <Label className="text-sm text-slate-500">Contact Person</Label>
                            {isEditing ? (
                              <Input
                                value={editForm.contact_person || ''}
                                onChange={(e) => setEditForm({...editForm, contact_person: e.target.value})}
                                className="mt-1"
                              />
                            ) : (
                              <p className="font-medium text-slate-900 mt-1">{selectedCompany.contact_person}</p>
                            )}
                          </div>
                          
                          {/* Email */}
                          <div>
                            <Label className="text-sm text-slate-500">Email</Label>
                            {isEditing ? (
                              <Input
                                type="email"
                                value={editForm.email || ''}
                                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                className="mt-1"
                              />
                            ) : (
                              <p className="font-medium text-slate-900 mt-1">{selectedCompany.email}</p>
                            )}
                          </div>
                          
                          {/* Phone */}
                          <div>
                            <Label className="text-sm text-slate-500">Phone</Label>
                            {isEditing ? (
                              <Input
                                value={editForm.phone || ''}
                                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                                className="mt-1"
                              />
                            ) : (
                              <p className="font-medium text-slate-900 mt-1">{selectedCompany.phone}</p>
                            )}
                          </div>
                          
                          {/* Address */}
                          <div className="md:col-span-2">
                            <Label className="text-sm text-slate-500">Address</Label>
                            {isEditing ? (
                              <Input
                                value={editForm.address || ''}
                                onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                                className="mt-1"
                              />
                            ) : (
                              <p className="font-medium text-slate-900 mt-1">{selectedCompany.address}</p>
                            )}
                          </div>
                          
                          {/* Website */}
                          <div className="md:col-span-2">
                            <Label className="text-sm text-slate-500">Website</Label>
                            {isEditing ? (
                              <Input
                                value={editForm.website || ''}
                                onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                                className="mt-1"
                              />
                            ) : (
                              <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline mt-1 block">
                                {selectedCompany.website}
                              </a>
                            )}
                          </div>
                          
                          {/* Service Cities */}
                          <div>
                            <Label className="text-sm text-slate-500">Service Cities (comma-separated)</Label>
                            {isEditing ? (
                              <Input
                                value={editForm.cities || ''}
                                onChange={(e) => setEditForm({...editForm, cities: e.target.value})}
                                className="mt-1"
                                placeholder="Toronto, Ottawa, Montreal"
                              />
                            ) : (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {selectedCompany.cities.map((city, i) => (
                                  <Badge key={i} variant="outline" className="bg-blue-50">
                                    {city}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Postal Code Coverage */}
                          <div>
                            <Label className="text-sm text-slate-500">Postal Code Prefixes (comma-separated)</Label>
                            {isEditing ? (
                              <Input
                                value={editForm.postal_code_prefixes || ''}
                                onChange={(e) => setEditForm({...editForm, postal_code_prefixes: e.target.value})}
                                className="mt-1"
                                placeholder="M5V, K2M, H3B"
                              />
                            ) : (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {selectedCompany.postal_code_prefixes.map((pc, i) => (
                                  <Badge key={i} variant="outline" className="bg-purple-50">
                                    {pc}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Management Types */}
                          <div className="md:col-span-2">
                            <Label className="text-sm text-slate-500 font-semibold">Property Management Types</Label>
                            {isEditing ? (
                              <div className="mt-3 space-y-2">
                                {/* Select All Button */}
                                <div className="mb-3">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const allTypes = ['Residential', 'Condo', 'Commercial', 'Multi-Family'];
                                      const currentTypes = editForm.management_types ? 
                                        (typeof editForm.management_types === 'string' ? 
                                          editForm.management_types.split(',').map(t => t.trim()) : 
                                          editForm.management_types) : 
                                        selectedCompany.management_types || [];
                                      
                                      // If all are selected, deselect all. Otherwise, select all
                                      const allSelected = allTypes.every(type => currentTypes.includes(type));
                                      setEditForm({
                                        ...editForm, 
                                        management_types: allSelected ? '' : allTypes.join(', ')
                                      });
                                    }}
                                    className="text-xs"
                                  >
                                    {(() => {
                                      const allTypes = ['Residential', 'Condo', 'Commercial', 'Multi-Family'];
                                      const currentTypes = editForm.management_types ? 
                                        (typeof editForm.management_types === 'string' ? 
                                          editForm.management_types.split(',').map(t => t.trim()) : 
                                          editForm.management_types) : 
                                        selectedCompany.management_types || [];
                                      const allSelected = allTypes.every(type => currentTypes.includes(type));
                                      return allSelected ? '❌ Deselect All' : '✅ Select All';
                                    })()}
                                  </Button>
                                </div>
                                
                                {['Residential', 'Condo', 'Commercial', 'Multi-Family'].map(type => {
                                  const currentTypes = editForm.management_types ? 
                                    (typeof editForm.management_types === 'string' ? 
                                      editForm.management_types.split(',').map(t => t.trim()) : 
                                      editForm.management_types) : 
                                    selectedCompany.management_types || [];
                                  
                                  const isChecked = currentTypes.includes(type);
                                  
                                  return (
                                    <div key={type} className="flex items-center">
                                      <input
                                        type="checkbox"
                                        id={`type-${type}`}
                                        checked={isChecked}
                                        onChange={(e) => {
                                          let newTypes = [...currentTypes];
                                          if (e.target.checked) {
                                            if (!newTypes.includes(type)) {
                                              newTypes.push(type);
                                            }
                                          } else {
                                            newTypes = newTypes.filter(t => t !== type);
                                          }
                                          setEditForm({...editForm, management_types: newTypes.join(', ')});
                                        }}
                                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                      />
                                      <label htmlFor={`type-${type}`} className="ml-2 text-sm text-gray-700 cursor-pointer">
                                        {type}
                                      </label>
                                    </div>
                                  );
                                })}
                                <p className="text-xs text-slate-400 mt-2">Select all property types this company can manage</p>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {selectedCompany.management_types?.map((type, i) => (
                                  <Badge key={i} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                    {type}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Service Radius */}
                          <div>
                            <Label className="text-sm text-slate-500">Service Radius (km)</Label>
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editForm.service_radius_km || 50}
                                onChange={(e) => setEditForm({...editForm, service_radius_km: parseInt(e.target.value)})}
                                className="mt-1"
                              />
                            ) : (
                              <p className="font-medium text-slate-900 mt-1">{selectedCompany.service_radius_km || 50} km</p>
                            )}
                          </div>
                          
                          {/* Description */}
                          <div className="md:col-span-2">
                            <Label className="text-sm text-slate-500">Description</Label>
                            {isEditing ? (
                              <Textarea
                                value={editForm.description || ''}
                                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                                className="mt-1"
                                rows={3}
                              />
                            ) : (
                              <p className="text-slate-900 mt-1">{selectedCompany.description || 'No description'}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="border-t pt-4 space-y-3">
                          {selectedCompany.approval_status === "pending" && (
                            <div className="flex gap-3">
                              <Button
                                onClick={() => handleApproval(selectedCompany.id, "approved")}
                                className="flex-1 bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleApproval(selectedCompany.id, "rejected")}
                                variant="outline"
                                className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </div>
                          )}
                          
                          {selectedCompany.approval_status === "approved" && selectedCompany.payment_status !== "active" && (
                            <Button
                              onClick={() => handlePaymentUpdate(selectedCompany.id, "paid")}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              data-testid="mark-paid-button"
                            >
                              <DollarSign className="h-4 w-4 mr-2" />
                              Mark as Paid & Activate
                            </Button>
                          )}
                          
                          {/* Reset Password & Delete Buttons */}
                          <div className="border-t pt-3 mt-3 space-y-2">
                            <Button
                              onClick={() => {
                                setResetPasswordCompany(selectedCompany);
                                setResetPasswordDialogOpen(true);
                              }}
                              variant="outline"
                              className="w-full border-green-400 text-green-700 hover:bg-green-50 hover:border-green-500"
                            >
                              🔑 Reset Password
                            </Button>
                            
                            <Button
                              onClick={() => handleDeleteCompany(selectedCompany.id, selectedCompany.company_name)}
                              variant="outline"
                              className="w-full border-blue-400 text-blue-700 hover:bg-blue-50 hover:border-blue-500"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Delete Company Permanently
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password for {resetPasswordCompany?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Company Email: <span className="font-semibold">{resetPasswordCompany?.email}</span>
            </p>
            
            {/* Method Selection */}
            <div className="space-y-3">
              <Label>Choose Reset Method:</Label>
              <div className="space-y-2">
                <button
                  onClick={() => setResetMethod("email")}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    resetMethod === "email" 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">📧</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">Send Reset Email</div>
                      <div className="text-sm text-gray-600">Company will receive an email with a reset link</div>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => setResetMethod("manual")}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    resetMethod === "manual" 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🔑</div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">Set New Password</div>
                      <div className="text-sm text-gray-600">Manually set a new password for this company</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Manual Password Input (only show if manual selected) */}
            {resetMethod === "manual" && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="text"
                  placeholder="Enter new password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={resetMethod === "email" ? handleSendResetEmail : handleResetPassword}
                className="flex-1"
                style={{ backgroundColor: '#3b82f6' }}
              >
                {resetMethod === "email" ? "📧 Send Email" : "🔑 Set Password"}
              </Button>
              <Button
                onClick={() => {
                  setResetPasswordDialogOpen(false);
                  setResetPasswordCompany(null);
                  setNewPassword("");
                  setResetMethod("email");
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}